import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  chakra,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
} from '@chakra-ui/react';

import { Tracker } from '@izimi/modules';
import { INPUT_VALUE_VALIDATOR } from '@izimi/utils';

import { TextArea } from '../TextArea';
import { useTracking } from '../tracking';

const Form = chakra('form');

type FormValues = {
  description: string;
  withMetadata: boolean;
};

type Props = {
  email: string;
};

export const HelpReportProblemForm = ({ email }: Props) => {
  const { t } = useTranslation('help');
  const { trackEvent } = useTracking();

  const {
    handleSubmit,
    formState: { errors },
    watch,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      description: '',
      withMetadata: false,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const userAgent = navigator?.userAgent;
    const newLine = '%0D%0A';

    trackEvent({
      category: Tracker.Category.Profile,
      action: Tracker.Action.ReportedAProblem,
    });

    let body = `${values.description}${newLine}${newLine}`;
    if (values.withMetadata) {
      if (userAgent) {
        body += `${t('reportProblem.userAgent')}: ${userAgent}${newLine}`;
      }
    }

    const subject = `[izimi] ${t('reportProblem.problemReport')}`;

    window.document.location = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <Form mb={7} onSubmit={handleSubmit(onSubmit)} id='reportProblemForm'>
        <FormControl mb={5} isInvalid={Boolean(errors?.description)}>
          <FormLabel>{t('reportProblem.description')}</FormLabel>
          <TextArea
            rows={8}
            maxCharacters={300}
            value={watch('description')}
            placeholder={t('reportProblem.descriptionPlaceholder')}
            {...register('description', {
              required: {
                value: true,
                message: t('shared:form.validation.required'),
              },
              maxLength: {
                value: 300,
                message: t('shared:form.validation.maxCharacters', {
                  max: 300,
                }),
              },
              pattern: {
                value: INPUT_VALUE_VALIDATOR,
                message: t('shared:form.validation.invalidCharacter'),
              },
            })}
          />
          <FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <Checkbox {...register('withMetadata')}>
            <Text whiteSpace='normal'>{t('reportProblem.withMetadata')}</Text>
          </Checkbox>
        </FormControl>
      </Form>
      <Box mt='auto' mb={-5}>
        <Divider mb={1} />
        <Button
          variant='registrationPrimary'
          w='100%'
          title={t(`shared:action.send`)}
          type='submit'
          form='reportProblemForm'>
          {t(`shared:action.send`)}
        </Button>
      </Box>
    </>
  );
};
